import React from 'react';
import type { TwoFactorEnrollment } from '../api/twoFactorApiClient';

/**
 * The two presentational blocks shared by both 2FA enrolment surfaces:
 * the signed-in Security page and the signed-out bootstrap screen
 * (TwoFactorEnrollmentForm).
 *
 * Only the parts that are genuinely identical live here. The code input and the
 * action buttons deliberately do NOT — the dashboard uses `Input`/`Button` while
 * the auth screens use `AuthInput` and full-width buttons, and forcing one of
 * those on the other would make both screens worse to save a few lines.
 */

interface TwoFactorQrPanelProps {
  enrollment: TwoFactorEnrollment;
}

/**
 * QR plus the manual-entry fallback. The fallback is not optional polish:
 * desktop authenticators and anyone scanning from the same device they are
 * signed in on cannot use the image at all.
 */
export const TwoFactorQrPanel: React.FC<TwoFactorQrPanelProps> = ({ enrollment }) => (
  <div className="space-y-3">
    <img
      src={enrollment.qrDataUri}
      alt="Two-step verification QR code"
      /* Explicit white plate: the QR is a transparent-background PNG, and in dark
         mode an unplated code loses the contrast scanners rely on. */
      className="w-44 h-44 rounded-md border border-border bg-white p-2"
    />
    <div className="text-sm">
      <span className="text-muted-foreground">Can&apos;t scan? Enter this key: </span>
      <code className="font-mono text-foreground break-all">{enrollment.secret}</code>
    </div>
  </div>
);

interface BackupCodesPanelProps {
  codes: string[];
  acknowledged: boolean;
  onAcknowledgedChange: (next: boolean) => void;
}

/**
 * The one-time backup codes plus the acknowledgement gate.
 *
 * The checkbox is a real safety control, not ceremony: the server hashes these
 * and will never show them again, so whatever dismisses this panel destroys the
 * only copy. Callers must keep their "continue" action disabled until
 * `acknowledged` is true.
 */
export const BackupCodesPanel: React.FC<BackupCodesPanelProps> = ({
  codes,
  acknowledged,
  onAcknowledgedChange,
}) => (
  <div className="space-y-4">
    <p className="text-sm text-foreground">
      Save these backup codes somewhere safe. Each one works once, and this is the only time
      they are shown.
    </p>
    <ul className="grid grid-cols-2 gap-2 font-mono text-sm">
      {codes.map((c) => (
        <li key={c} className="rounded border border-border bg-muted px-3 py-2">
          {c}
        </li>
      ))}
    </ul>
    <label className="flex items-center gap-2 text-sm text-foreground">
      <input
        type="checkbox"
        checked={acknowledged}
        onChange={(e) => onAcknowledgedChange(e.target.checked)}
      />
      I have saved these codes
    </label>
  </div>
);
